import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Capacitor } from '@capacitor/core';

const BYPASS_KEY = 'bd_preview_bypass';
const PREVIEW_TOKEN = 'brainday-preview';

function isBypassed(): boolean {
  try {
    return localStorage.getItem(BYPASS_KEY) === '1';
  } catch {
    return false;
  }
}

function setBypass(): void {
  try {
    localStorage.setItem(BYPASS_KEY, '1');
  } catch {
    // ignore
  }
}

function hasPreviewToken(search: string): boolean {
  // Check query string (?preview=TOKEN) — stripped by Safari's Link Tracking Protection
  if (new URLSearchParams(search).get('preview') === PREVIEW_TOKEN) return true;
  // Check hash directly from window.location — React Router's useLocation() may not
  // expose hash reliably in Safari. Support both #preview=TOKEN and #TOKEN forms.
  const rawHash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
  if (new URLSearchParams(rawHash).get('preview') === PREVIEW_TOKEN) return true;
  if (rawHash === PREVIEW_TOKEN) return true;
  return false;
}

/**
 * Pre-launch web lockdown. The marketing site (brainday.app) is live for
 * awareness, but real sign-in / sign-up and the web app itself are disabled on
 * the web until the App Store launch — the routes this wraps (`/login`,
 * `/onboarding/*`, `/app/*`) redirect to the Coming Soon page on the web.
 *
 * Native (Capacitor) builds and preview-token holders pass straight through, so
 * the iOS app keeps working and the screenshot harness (which sets the
 * `bd_preview_bypass` localStorage flag) still renders app routes.
 *
 * TODO(Phase 17 — launch): lift this lockdown (let the web sign in / use the app
 * again) when going live. Tracked in docs/app-store-submission-checklist.md.
 */
export function WebAppGuard() {
  const location = useLocation();
  const navigate = useNavigate();

  const hasToken = hasPreviewToken(location.search);

  // Persist bypass before first render so isBypassed() below sees it
  if (hasToken) setBypass();

  const allowed = Capacitor.isNativePlatform() || hasToken || isBypassed();

  // Strip the token from the URL after mounting — navigation is a side effect
  useEffect(() => {
    if (!hasToken) return;
    const nextSearch = new URLSearchParams(location.search);
    nextSearch.delete('preview');
    const nextHash = new URLSearchParams(location.hash.replace(/^#/, ''));
    nextHash.delete('preview');
    const hash = nextHash.toString() ? `#${nextHash.toString()}` : '';
    void navigate(
      {
        pathname: location.pathname,
        search: nextSearch.toString() ? `?${nextSearch.toString()}` : '',
        hash,
      },
      { replace: true },
    );
  }, [hasToken, location.pathname, location.search, location.hash, navigate]);

  if (!allowed) return <Navigate to="/coming-soon" replace />;
  return <Outlet />;
}

export default WebAppGuard;
